/**
 * 상세 수수료율 — 의약품 추가 모달 (레거시 detailFeeRateSetting 인라인 Modal 포팅)
 * 레거시 Table pagination(pageSize:10)에 맞춰 서버 페이징 + SimplePagination.
 */
import {
  drugService,
  type DrugInfoResponse,
} from '@/entities/drug/api/drug.api';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { useListPagination } from '@/shared/hooks/useListPagination';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export interface DetailFeeDrugSelection {
  /** 의약품 마스터 ID — 상세 수수료 저장 API가 drugId를 필수로 요구한다 */
  drugId: number | null;
  drugCode: string;
  drugName: string;
  pharmaCompanyName?: string;
}

interface DetailFeeDrugAddModalProps {
  open: boolean;
  pharmaCompanyName?: string;
  existingDrugCodes?: string[];
  onClose: () => void;
  onConfirm: (drugs: DetailFeeDrugSelection[]) => void;
}

/** 레거시 모달 Table pageSize와 동일 */
const DRUG_MODAL_PAGE_SIZE = 10;

const getPreviousRefYm = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}${m}`;
};

export const DetailFeeDrugAddModal: React.FC<DetailFeeDrugAddModalProps> = ({
  open,
  pharmaCompanyName,
  existingDrugCodes = [],
  onClose,
  onConfirm,
}) => {
  const toast = useToast();
  const { page, setPage, pageSize, pageable } =
    useListPagination(DRUG_MODAL_PAGE_SIZE);

  const [keyword, setKeyword] = useState('');
  /** 실제 조회에 쓰는 검색어 — 검색 버튼/엔터 시에만 반영 */
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [searchNonce, setSearchNonce] = useState(0);
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState<DrugInfoResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState<
    Record<string, DetailFeeDrugSelection>
  >({});

  const existingSet = useMemo(
    () => new Set(existingDrugCodes.map((code) => code.trim())),
    [existingDrugCodes],
  );

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setAppliedKeyword('');
      setSearchNonce(0);
      setCatalog([]);
      setSelected({});
      setTotalCount(0);
      setPage(1);
      return;
    }

    if (!pharmaCompanyName) {
      setCatalog([]);
      setTotalCount(0);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const kw = appliedKeyword.trim();
        const isNumeric = /^\d+$/.test(kw);
        const response = await drugService.getDrugs(
          {
            refYm: getPreviousRefYm(),
            pharmaCompanyName,
            ...(kw
              ? isNumeric
                ? { drugCode: kw }
                : { drugName: kw }
              : {}),
          },
          pageable,
        );
        if (cancelled) return;
        // 페이지 내 중복만 제거 (서버 totalCount는 그대로 사용)
        const unique = new Map<string, DrugInfoResponse>();
        (response.content ?? []).forEach((item) => {
          const key = `${item.drugCode}-${item.drugName}`;
          if (!unique.has(key)) unique.set(key, item);
        });
        setCatalog([...unique.values()]);
        setTotalCount(Number(response.totalCount ?? 0));
      } catch (err) {
        if (cancelled) return;
        handleApiError(err, toast);
        setCatalog([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // toast는 매 렌더 새 객체라 deps 제외 (무한 fetch 방지)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchNonce로 동일 조건 재검색
  }, [
    open,
    pharmaCompanyName,
    pageable.page,
    pageable.size,
    appliedKeyword,
    searchNonce,
    setPage,
  ]);

  const handleSearch = () => {
    const next = keyword.trim();
    setPage(1);
    setAppliedKeyword(next);
    // 같은 검색어·1페이지에서도 재조회되도록
    setSearchNonce((n) => n + 1);
  };

  const toggleRow = (item: DrugInfoResponse, checked: boolean) => {
    const code = item.drugCode;
    if (existingSet.has(code)) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) {
        next[code] = {
          drugId: item.id ?? null,
          drugCode: code,
          drugName: item.drugName,
          pharmaCompanyName: item.pharmaCompanyName,
        };
      } else {
        delete next[code];
      }
      return next;
    });
  };

  const selectedList = Object.values(selected);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* overflow는 테이블 영역에만 — 다이얼로그 밖으로 테이블이 넘치지 않게 */}
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-4 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>의약품 추가</DialogTitle>
        </DialogHeader>

        {!pharmaCompanyName ? (
          <p className="text-sm text-muted-foreground">
            제약사를 먼저 선택해 주세요.
          </p>
        ) : (
          <>
            <FilterRow
              gap={12}
              className="w-full shrink-0"
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
              }}
            >
              <div className="min-w-0 flex-1 space-y-1">
                <Label>검색 (의약품명 또는 코드)</Label>
                <Input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="submit"
                className="ruda-button shrink-0"
                disabled={loading}
              >
                <Search className="mr-2 h-4 w-4" />
                검색
              </Button>
            </FilterRow>

            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border">
              {/* 페이지 전환 시 테이블을 유지해 레이아웃 깜빡임 방지 */}
              <div
                className={cn(
                  'min-h-0 flex-1 overflow-auto transition-opacity',
                  loading && catalog.length > 0 && 'pointer-events-none opacity-50',
                )}
              >
                {loading && catalog.length === 0 ? (
                  <div className="flex min-h-[200px] items-center justify-center py-12">
                    <LoadingSpinner tip="의약품 목록을 불러오는 중..." />
                  </div>
                ) : (
                  <Table
                    className="min-w-0 table-fixed"
                    containerClassName="overflow-visible"
                  >
                    <TableHeader className="sticky top-0 z-10 bg-white">
                      <TableRow>
                        <TableHead className="w-14">선택</TableHead>
                        <TableHead>의약품명</TableHead>
                        <TableHead className="w-36">코드</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalog.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className="text-center text-muted-foreground"
                          >
                            검색 결과가 없습니다.
                          </TableCell>
                        </TableRow>
                      ) : (
                        catalog.map((item) => {
                          const disabled = existingSet.has(item.drugCode);
                          const checked = Boolean(selected[item.drugCode]);
                          return (
                            <TableRow
                              key={`${item.drugCode}-${item.drugName}`}
                            >
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    toggleRow(item, e.target.checked)
                                  }
                                />
                              </TableCell>
                              <TableCell
                                className="truncate"
                                title={item.drugName}
                              >
                                {item.drugName}
                              </TableCell>
                              <TableCell
                                className="truncate"
                                title={item.drugCode}
                              >
                                {item.drugCode}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>

              <SimplePagination
                size="sm"
                page={page}
                pageSize={pageSize}
                total={totalCount}
                onPageChange={setPage}
                className="shrink-0 border-x-0 border-b-0"
              />
            </div>
          </>
        )}

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button
            onClick={() => {
              onConfirm(selectedList);
              onClose();
            }}
            disabled={selectedList.length === 0}
          >
            추가 ({selectedList.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
